'use client';

import { FormattedAccordion } from 'components/Accordion';
import PrintButton from 'components/buttons/PrintButton';
import ContentCard from 'components/content-library/ContentCard';
import FilterStack from 'components/FilterStack';
import GraphicDateTimeline from 'components/GraphicDateTimeline';
import VideoPlayer from 'components/media/VideoPlayer';
import { LibraryPageFields } from 'types/content';
import { useAppDispatch, useAppSelector } from 'lib/hooks';
import { RootState } from 'lib/store';
import Link from 'next/link';
import { useEffect } from 'react';
import { setUserInJourney } from 'slices/navigationSlice';
import { ContentCardItem } from 'types/content';
import { selectVideoSource } from 'utils/mediaUtils';
import { contentWithData } from 'utils/content';
import { selectUserDynamicValues } from 'slices/dynamicDataSelectors';
import { UserDynamicValues } from 'types/user';
import BackButton from 'app/cart/BackButton';

const LibraryComponent = ({ data }: LibraryPageFields) => {
  /* @TODO Only pull relevant User Data from the Store */
  const userData = useAppSelector((state: RootState) =>
    selectUserDynamicValues(state)
  ) as UserDynamicValues;
  const dispatch = useAppDispatch();

  // Get component data
  const accordions = data.elements.accordions?.value || [];
  const body = data.elements.body?.value;
  const externalCtaText = data.elements.external_cta_text?.value;
  const externalCtaUrl = data.elements.external_cta_url?.value;
  const iepChart = data.elements.iep_chart?.value;
  const libraryCards = data.elements.library_cards?.value || [];
  const title = data.elements.title?.value;
  const video = data.elements.video?.value;

  useEffect(() => {
    // Note: setGlobalCCButtonDisplayed no longer exists in mediaSlice
    dispatch(setUserInJourney(false));
  }, [dispatch]);

  const contentCards = (): ContentCardItem[] => {
    return libraryCards;
  };

  const filteredAccordions = (): FormattedAccordion[] | undefined => {
    return accordions;
  };

  const cards = contentCards();

  return (
    <section className={`container-xl main-wrapper library-page `}>
      <div>
        <div className="tighten">
          <nav className="button-header">
            <BackButton />
            <PrintButton />
          </nav>
          {title && (
            <h1 data-testid="library-page-header-text">
              {contentWithData(title, userData)}
            </h1>
          )}

          {video && (
            <div className="mx-auto">
              <VideoPlayer
                dynamicText={video.dynamicText?.url}
                source={selectVideoSource(video)}
                stillImage={video.videoStillImage?.url}
                track={video.closedCaption?.url}
                transcript={video.videoTranscript}
              />
            </div>
          )}

          {accordions && (
            <FilterStack accordions={filteredAccordions() || []} />
          )}

          {iepChart && userData.birthdate && (
            <GraphicDateTimeline birthdate={userData.birthdate} extended />
          )}

          {body && (
            <span className="append-dot">
              {contentWithData(body, userData)}
              {externalCtaUrl && externalCtaText && (
                <div className="external-cta">
                  <Link
                    className="btn btn-secondary"
                    href={externalCtaUrl}
                    target="_blank"
                  >
                    {externalCtaText}
                  </Link>
                </div>
              )}
            </span>
          )}
        </div>
      </div>
      <div className="card-grid">
        {cards && (
          <div className="row">
            {cards.map((card) => (
              <div className="col-md-6" key={`${card.title}-${card.slug}`}>
                <ContentCard
                  gatedConditions={card.gatedConditions}
                  gatedConditionText={card.gatedConditionText}
                  gatedContentButtonDestination={
                    card.gatedContentButtonDestination
                  }
                  icon={card.icon}
                  slug={card.slug}
                  title={card.title}
                />
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default LibraryComponent;
